import { ref, reactive } from 'vue';

import { mutationExecute } from '@/Utils/graphqlAPI';

import util from '@/Utils/util';
import { motoStripeService } from '@/use/motoStripeService';
import type { StripeConfirmPaymentResult } from '@/use/stripePaymentIntents';
import type { CreateSetupIntentMutationVariables } from '@/gql/graphql';
import { graphql } from '@/gql';

declare const Stripe: any;
const stripe = ref(null as any);

export type StripeIntentKind = 'setup' | 'payment';

let currentActiveStripeSetupIntentId: string | null = null;
let currentUseMoto = false;
let currentCallerId: string | null = null;
let currentIntentKind: StripeIntentKind = 'setup';

const paymentElement = reactive({ elem: null as any });
const elements = reactive({ v: null as any });

export const clearStripe = () => {
  paymentElement.elem?.clear();
};

export const terminateStripe = () => {
  paymentElement.elem?.unmount();
  paymentElement.elem = null;
  elements.v = null;
  currentActiveStripeSetupIntentId = null;
  currentUseMoto = false;
  currentCallerId = null;
  currentIntentKind = 'setup';
};

export const handleStripeError = (error: unknown, isForOperator: boolean = true) => {
  const errorMessage = error instanceof Error ? error.message : String(error);

  if (errorMessage.includes('Stripe is not defined')) {
    return `
      There appears to be an issue with your network or DNS settings.
      </br>
      Please update them using <a href="https://developers.cloudflare.com/1.1.1.1/encryption/dns-over-https/encrypted-dns-browsers/" target="_blank">this link</a>.
    `;
  }

  return `${isForOperator ? 'For @eng - ' : ''}${errorMessage}`;
};

async function createSetupIntent(variables: CreateSetupIntentMutationVariables) {
  const response = await mutationExecute({
    mutation: graphql(`
      mutation CreateSetupIntent(
        $use3ds: Boolean
        $stripeAccountName: String
        $callerId: String
        $plan: String
        $subscriptionStartAt: String
      ) {
        createSetupIntent(
          use3ds: $use3ds
          stripeAccountName: $stripeAccountName
          callerId: $callerId
          plan: $plan
          subscriptionStartAt: $subscriptionStartAt
        ) {
          id
          clientSecret
          useMoto
          intentKind
        }
      }
    `),
    variables,
    fetchPolicy: 'no-cache',
  });

  return {
    clientSecret: response.data?.createSetupIntent.clientSecret ?? null,
    setupIntentId: response.data?.createSetupIntent.id ?? null,
    useMoto: Boolean(response.data?.createSetupIntent.useMoto),
    intentKind: response.data?.createSetupIntent.intentKind === 'payment' ? 'payment' : ('setup' as StripeIntentKind),
  };
}

/// https://docs.stripe.com/js/setup_intents/confirm_setup#confirm_setup_intent-options-confirmParams-payment_method_data-billing_details
export interface BillingDetails {
  name?: string;
  email?: string;
  phone?: string;

  // https://docs.stripe.com/api/payment_methods/create#create_payment_method-billing_details
  address?: {
    city: string;
    country: string;
    line1: string;
    line2: string;
    postal_code: string;
    state: string;
  };
}

/**

StripeConfirmSetupResult -  https://docs.stripe.com/js/setup_intents/confirm_setup

The docs don't provdide example, therefore -

Success:
{
  "setupIntent": {
    "id": "seti_1QZZ3PEME16FzHUfqVAZDwOS",
    "object": "setup_intent",
    "automatic_payment_methods": null,
    "cancellation_reason": null,
    "client_secret": "seti_1QZZ3PEME16FzHUfqVAZDwOS_secret_RSU1RLKZHHp3UnqO4nyvUbWI6BHowx9",
    "created": 1735050347,
    "description": null,
    "last_setup_error": null,
    "livemode": false,
    "next_action": null,
    "payment_method": "pm_1QZZ4REME16FzHUfFSuQnyVj",
    "payment_method_configuration_details": null,
    "payment_method_types": [
        "card"
    ],
    "status": "succeeded",
    "usage": "off_session"
  }
}

Failure:
{
  "error": {
    "type": "card_error",
    "code": "card_declined",
    "decline_code": "generic_decline",
    "doc_url": "https://stripe.com/docs/error-codes/card-declined",
    "message": "Your card has been declined.",
    "payment_method": {
      "id": "pm_1QZZ6wEME16FzHUfPCgMHzsj",
      "object": "payment_method",
      "allow_redisplay": "unspecified",
      "billing_details": {
          "address": {
              "city": "",
              "country": "US",
              "line1": "",
              "line2": "",
              "postal_code": "12345",
              "state": ""
          },
          "email": null,
          "name": "Bob",
          "phone": null
      },
      "card": {
          "brand": "visa",
          "checks": {
              "address_line1_check": null,
              "address_postal_code_check": null,
              "cvc_check": null
          },
          "country": "US",
          "display_brand": "visa",
          "exp_month": 2,
          "exp_year": 2025,
          "funding": "credit",
          "generated_from": null,
          "last4": "0002",
          "networks": {
              "available": [
                  "visa"
              ],
              "preferred": null
          },
          "regulated_status": "unregulated",
          "three_d_secure_usage": {
              "supported": true
          },
          "wallet": null
      },
      "created": 1735050566,
      "customer": null,
      "livemode": false,
      "type": "card"
    },
    "request_log_url": "https://dashboard.stripe.com/test/logs/req_lSqUuu5y5a9sMc?t=1735050566",
    "setup_intent": {
        "id": "seti_1QZZ6fEME16FzHUfi5oRQ9l8",
        "object": "setup_intent",
        "automatic_payment_methods": null,
        "cancellation_reason": null,
        "client_secret": "seti_1QZZ6fEME16FzHUfi5oRQ9l8_secret_RSU4yZnYzGz8i9QvqGEZ4F8whOeH7wn",
        "created": 1735050549,
        "description": null,
        "last_setup_error": {
            "code": "card_declined",
            "decline_code": "generic_decline",
            "doc_url": "https://stripe.com/docs/error-codes/card-declined",
            "message": "Your card was declined.",
            "payment_method": {
                "id": "pm_1QZZ6wEME16FzHUfPCgMHzsj",
                "object": "payment_method",
                "allow_redisplay": "unspecified",
                "billing_details": {
                    "address": {
                        "city": "",
                        "country": "US",
                        "line1": "",
                        "line2": "",
                        "postal_code": "12345",
                        "state": ""
                    },
                    "email": null,
                    "name": "Bob",
                    "phone": null
                },
                "card": {
                    "brand": "visa",
                    "checks": {
                        "address_line1_check": null,
                        "address_postal_code_check": null,
                        "cvc_check": null
                    },
                    "country": "US",
                    "display_brand": "visa",
                    "exp_month": 2,
                    "exp_year": 2025,
                    "funding": "credit",
                    "generated_from": null,
                    "last4": "0002",
                    "networks": {
                        "available": [
                            "visa"
                        ],
                        "preferred": null
                    },
                    "regulated_status": "unregulated",
                    "three_d_secure_usage": {
                        "supported": true
                    },
                    "wallet": null
                },
                "created": 1735050566,
                "customer": null,
                "livemode": false,
                "type": "card"
            },
            "type": "card_error"
        },
        "livemode": false,
        "next_action": null,
        "payment_method": null,
        "payment_method_configuration_details": null,
        "payment_method_types": [
            "card"
        ],
        "status": "requires_payment_method",
        "usage": "off_session"
    },
    "shouldRetry": false
  }
}

Another Failure:

{
  "error": {
    "code": "incorrect_number",
    "doc_url": "https://stripe.com/docs/error-codes/incorrect-number",
    "message": "Your card number is incorrect.",
    "param": "number",
    "request_log_url": "https://dashboard.stripe.com/logs/req_2TQj448fJipqZD?t=1737174943",
    "type": "card_error"
  }
}

*/
interface StripeConfirmSetupResult {
  setupIntent?: {
    id: string;
  };
  error?: {
    setup_intent?: {
      id: string;
    };
    code: string;
    message: string;
    decline_code?: string;
  };
}

export type ConfirmSetupResult = {
  setupIntentId: string;
  confirmationTokenId?: string;
  /** Which Stripe intent this id belongs to: 'setup' (SetupIntent) or 'payment' (registration PaymentIntent). */
  intentKind: StripeIntentKind;
  error?: { code: string; message: string; decline_code?: string };
};

/**
 * no_preauth_retest: createSetupIntent handed us a manual-capture membership PaymentIntent instead
 * of a SetupIntent, so the card has to be confirmed with confirmPayment. Success only authorizes
 * (requires_capture) -- addPaymentSource captures it and saves the card. 3DS is handled inline by
 * redirect: 'if_required', so the backend never sees a requires_action here.
 */
async function confirmRegistrationPaymentIntent(paymentIntentId: string, billingDetails?: BillingDetails): Promise<ConfirmSetupResult> {
  const result: StripeConfirmPaymentResult = await stripe.value.confirmPayment({
    elements: elements.v,
    confirmParams: {
      return_url: util.getApiURL() + 'handle-stripe-redirect',

      payment_method_data: {
        billing_details: billingDetails,
      },
    },
    redirect: 'if_required',
  });

  if (result.error) {
    return {
      setupIntentId: result.error.payment_intent?.id || paymentIntentId,
      intentKind: 'payment',
      error: {
        code: result.error.code,
        message: result.error.message,
        decline_code: result.error.decline_code,
      },
    };
  }

  if (!result.paymentIntent) {
    throw new Error(`paymentIntent is not present in the result object.`);
  }

  return {
    setupIntentId: result.paymentIntent.id,
    intentKind: 'payment',
  };
}

async function confirmSetup(billingDetails?: BillingDetails): Promise<ConfirmSetupResult> {
  if (!currentActiveStripeSetupIntentId) {
    throw new Error(`currentActiveStripeSetupIntentId is null.`);
  }

  if (billingDetails?.name) {
    billingDetails.name = billingDetails.name.trim();
  }

  if (currentUseMoto && currentCallerId) {
    const motoResult = await motoStripeService.confirmMotoSetup(
      {
        stripe: stripe.value,
        elements: elements.v,
        setupIntentId: currentActiveStripeSetupIntentId,
      },
      billingDetails,
    );
    return { ...motoResult, intentKind: currentIntentKind };
  }

  if (currentIntentKind === 'payment') {
    return confirmRegistrationPaymentIntent(currentActiveStripeSetupIntentId, billingDetails);
  }

  const result: StripeConfirmSetupResult = await stripe.value.confirmSetup({
    elements: elements.v,
    confirmParams: {
      return_url: util.getApiURL() + 'handle-stripe-redirect',

      payment_method_data: {
        billing_details: billingDetails,
      },
    },
    redirect: 'if_required',
  });

  if (result.error) {
    return {
      setupIntentId: result.error.setup_intent?.id || currentActiveStripeSetupIntentId,
      intentKind: currentIntentKind,
      error: {
        code: result.error.code,
        message: result.error.message,
        decline_code: result.error.decline_code,
      },
    };
  }

  if (!result.setupIntent) {
    throw new Error(`setupIntent is not present in the result object.`);
  }
  return {
    setupIntentId: result.setupIntent!.id,
    intentKind: currentIntentKind,
  };
}

export const getStripeInstance = () => stripe.value;

export const currentSetupIntentId = () => {
  return currentActiveStripeSetupIntentId;
};

// onChange - https://docs.stripe.com/js/element/events/on_change?type=paymentElement
export interface StripeChangeEvent {
  elementType: string;
  complete: boolean;
  empty: boolean;
  collapsed: boolean;
}

export const useStripe = () => {
  const initStripe = async ({
    onChange,
    onReady,
    use3ds,
    stripeAccountName,
    callerId,
    isCustomerSelfPay = false,
    mountTo = '#payment-element',
    plan,
    subscriptionStartAt,
  }: {
    onChange: (events: StripeChangeEvent) => void;
    onReady: () => void;
    use3ds?: boolean;
    callerId?: string;
    isCustomerSelfPay?: boolean;
    stripeAccountName?: string;
    mountTo?: string;
    plan?: string;
    subscriptionStartAt?: string | null;
  }) => {
    stripe.value = Stripe(util.stripeKey[String(stripeAccountName)]);

    const { clientSecret, setupIntentId, useMoto, intentKind } = await createSetupIntent({
      use3ds,
      stripeAccountName,
      callerId,
      plan,
      subscriptionStartAt: subscriptionStartAt ?? null,
    });

    currentActiveStripeSetupIntentId = setupIntentId;
    currentUseMoto = useMoto;
    currentCallerId = callerId ?? null;
    currentIntentKind = intentKind;

    const appearance = {
      theme: 'stripe',

      variables: {
        colorText: '#32325',
        fontFamily: 'Muli, Arial, sans-serif',
        fontSizeBase: '16px',
      },
    };

    elements.v = stripe.value.elements({
      clientSecret,
      appearance,
    });

    // https://docs.stripe.com/js/elements_object/create_payment_element#payment_element_create-options
    const opts = {
      fields: {
        billingDetails: {
          address: 'never',
        },
      },
      wallets: {
        link: isCustomerSelfPay ? 'never' : 'auto',
        applePay: isCustomerSelfPay ? 'auto' : 'never',
        googlePay: isCustomerSelfPay ? 'auto' : 'never',
      },
    };
    paymentElement.elem = elements.v.create('payment', opts);
    paymentElement.elem.on('ready', () => {
      onReady();
    });
    paymentElement.elem.mount(mountTo);

    // Not calling removeEventListener as terminateStripe will unmount
    // the entire element and remove all event listeners
    paymentElement.elem.addEventListener('change', onChange);
  };

  return {
    initStripe,
    terminateStripe,
    clearStripe,
    confirmSetup,
    currentSetupIntentId,
    handleStripeError,
  };
};
